/**
 * T101: Tag List Component
 * List with inline CRUD operations for tags
 */

import { useState } from 'react'
import { Pencil, Trash2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useTags } from '@/hooks/useTags'
import { useCreateTag } from '@/hooks/useCreateTag'
import { useUpdateTag } from '@/hooks/useUpdateTag'
import { useDeleteTag } from '@/hooks/useDeleteTag'
import { TagFormDialog } from './tag-form-dialog'
import type { TagResponse } from '@/services/tags'

export function TagList() {
  const { data, isLoading } = useTags()
  const createMutation = useCreateTag()
  const updateMutation = useUpdateTag()
  const deleteMutation = useDeleteTag()

  const [formOpen, setFormOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<TagResponse | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<TagResponse | null>(null)

  const tags = data?.data ?? []

  const handleCreate = () => {
    setEditingTag(null)
    setFormOpen(true)
  }

  const handleEdit = (tag: TagResponse) => {
    setEditingTag(tag)
    setFormOpen(true)
  }

  const handleFormSubmit = async (formData: { name: string }) => {
    if (editingTag) {
      await updateMutation.mutateAsync({
        id: editingTag.id,
        input: formData,
      })
    } else {
      await createMutation.mutateAsync(formData)
    }
    setFormOpen(false)
    setEditingTag(null)
  }

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteMutation.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tags</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Tags</CardTitle>
          <Button size="sm" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Tag
          </Button>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tags yet. Create tags to label and filter your prompts.
            </p>
          ) : (
            <div className="divide-y">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between py-3"
                >
                  <Badge variant="secondary">{tag.name}</Badge>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(tag)}
                    >
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">Edit {tag.name}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(tag)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                      <span className="sr-only">Delete {tag.name}</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <TagFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        tag={editingTag}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This tag
              will be removed from all prompts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
